import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';

const ClientCalendarPage = () => {
  const { api } = useAuth();
  const [calendarItems, setCalendarItems] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendar();
  }, []);

  const fetchCalendar = async () => {
    try {
      const response = await api.get('/calendar');
      setCalendarItems(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getItemsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarItems.filter(item => item.date === dateStr);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-500';
      case 'scheduled': return 'bg-blue-500';
      case 'planned': return 'bg-yellow-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: 'IG',
      facebook: 'FB',
      youtube: 'YT',
      spotify: 'SP',
    };
    return icons[platform] || platform?.charAt(0).toUpperCase();
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const weekDays = getWeekDays();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="client-calendar">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-syne font-bold text-white">Content Calendar</h1>
            <p className="text-fp-textMuted mt-1">
              {weekDays[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateWeek(-1)}
              className="text-fp-textMuted hover:text-white"
              data-testid="prev-week"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              onClick={() => setCurrentDate(new Date())}
              className="text-fp-textMuted hover:text-white"
              data-testid="today-btn"
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateWeek(1)}
              className="text-fp-textMuted hover:text-white"
              data-testid="next-week"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-fp-surface border border-white/5 rounded-sm overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-7 border-b border-white/5">
            {weekDays.map((day, index) => {
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div
                  key={index}
                  className={`p-4 text-center border-r border-white/5 last:border-r-0 ${
                    isToday ? 'bg-primary/10' : ''
                  }`}
                >
                  <p className="text-fp-textMuted text-xs mb-1">{dayNames[index]}</p>
                  <p className={`text-lg font-semibold ${isToday ? 'text-primary' : 'text-white'}`}>
                    {day.getDate()}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Content Row */}
          <div className="grid grid-cols-7 min-h-[300px]">
            {weekDays.map((day, index) => {
              const items = getItemsForDate(day);
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div
                  key={index}
                  className={`p-2 border-r border-white/5 last:border-r-0 ${
                    isToday ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="space-y-2">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="p-2 bg-fp-bg rounded-sm"
                        data-testid={`calendar-item-${item.id}`}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <span className={`w-2 h-2 rounded-full ${getStatusColor(item.status)}`} />
                          <span className="text-fp-textMuted text-xs capitalize">
                            {getPlatformIcon(item.platform)}
                          </span>
                        </div>
                        <p className="text-white text-xs font-medium truncate">{item.title}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4">
          {[
            { status: 'published', label: 'Published' },
            { status: 'scheduled', label: 'Scheduled' },
            { status: 'planned', label: 'Planned' },
            { status: 'draft', label: 'Draft' },
          ].map((item) => (
            <div key={item.status} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${getStatusColor(item.status)}`} />
              <span className="text-fp-textMuted text-sm">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientCalendarPage;
